// Screen S1 — Login, identifier-first: EIN E-Mail-Feld für alle.
// @juunit.com-Adressen starten den Entra-OIDC-Flow (SSO); jede andere Adresse
// fordert einen Gast-Anmelde-Link an (POST /api/external/login-request).
// Die Antwort ist bewusst IMMER generisch — ob ein Gast-Zugang existiert,
// wird nie verraten (Anti-Enumeration, Entscheidung fällt serverseitig).

import { useState } from "preact/hooks";
import { ArrowRight, LogIn, MailCheck } from "lucide-preact";
import { probablyInTeams } from "../lib/teamsContext";

const INTERNAL_DOMAIN = "@juunit.com";

export function Login(): preact.JSX.Element {
  const inTeams = probablyInTeams();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkRequested, setLinkRequested] = useState(false);

  const startSso = (): void => {
    window.location.href = "/api/auth/login";
  };

  const submit = async (e: Event): Promise<void> => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr.includes("@") || busy) return;

    // Interne Adressen → Microsoft-Login (Entra-SSO).
    if (addr.endsWith(INTERNAL_DOMAIN)) {
      startSso();
      return;
    }

    // Alle anderen → Gast-Anmelde-Link anfordern. Antwort ist immer generisch.
    setBusy(true);
    try {
      await fetch("/api/external/login-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: addr }),
      });
    } catch {
      // Auch Netzwerkfehler nicht unterscheiden — generische Bestätigung.
    }
    setBusy(false);
    setLinkRequested(true);
  };

  return (
    <main class="min-h-dvh w-full flex items-center justify-center bg-gradient-to-br from-[color:var(--color-surface-container-low)] via-[color:var(--color-surface)] to-[color:var(--color-surface-container-high)] px-4">
      <section class="w-full max-w-md rounded-lg bg-[color:var(--color-surface-container-lowest)] shadow-level-2 p-6 sm:p-8 border border-[color:var(--color-outline-variant)]">
        <header class="mb-8 text-center">
          <p class="text-xs font-semibold tracking-widest uppercase text-[color:var(--color-primary)] mb-2">
            juunit GmbH
          </p>
          <h1 class="text-3xl font-bold text-[color:var(--color-on-surface)] tracking-tight">
            juuplan
          </h1>
          <p class="mt-2 text-sm text-[color:var(--color-on-surface-variant)]">
            Projekt-Roadmap-Tool
          </p>
        </header>

        {inTeams ? (
          <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] p-4 text-sm text-[color:var(--color-on-surface-variant)] text-center">
            Anmeldung über Teams wird vorbereitet…
            <br />
            <button
              type="button"
              onClick={() => window.location.reload()}
              class="mt-3 text-xs underline text-[color:var(--color-primary)]"
            >
              Neu laden
            </button>
          </div>
        ) : linkRequested ? (
          <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] p-4 text-sm text-[color:var(--color-on-surface)]">
            <div class="flex items-start gap-2.5">
              <MailCheck
                size={20}
                class="mt-0.5 shrink-0 text-[color:var(--color-primary)]"
                aria-hidden="true"
              />
              <div>
                <p class="font-medium">Prüfe dein Postfach</p>
                <p class="mt-1 text-[color:var(--color-on-surface-variant)]">
                  Falls für <span class="font-medium">{email.trim()}</span> ein
                  Gast-Zugang existiert, haben wir dir einen Anmelde-Link
                  geschickt. Der Link ist einmalig nutzbar.
                </p>
                <button
                  type="button"
                  onClick={() => setLinkRequested(false)}
                  class="mt-3 text-xs underline text-[color:var(--color-primary)]"
                >
                  Andere Adresse verwenden
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} class="space-y-3">
            <label class="block">
              <span class="text-xs font-medium text-[color:var(--color-on-surface-variant)]">
                E-Mail-Adresse
              </span>
              <input
                type="email"
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                placeholder="name@firma.de"
                required
                autocomplete="email"
                class="mt-1 w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-3 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="E-Mail-Adresse"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !email.trim().includes("@")}
              class="w-full inline-flex items-center justify-center gap-2 rounded bg-[color:var(--color-primary)] px-4 py-3 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface-container-lowest)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Sendet…" : "Weiter"}
              {!busy && <ArrowRight size={16} aria-hidden="true" />}
            </button>
            <button
              type="button"
              onClick={startSso}
              class="w-full inline-flex items-center justify-center gap-2 text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] underline underline-offset-2"
            >
              <LogIn size={14} aria-hidden="true" />
              Direkt mit Microsoft anmelden
            </button>
          </form>
        )}

        <footer class="mt-8 text-center text-xs text-[color:var(--color-on-surface-variant)]">
          Mitarbeiter melden sich mit ihrem{" "}
          <span class="font-medium">{INTERNAL_DOMAIN}</span>-Konto an, Gäste
          erhalten einen Anmelde-Link per E-Mail.
        </footer>
      </section>
    </main>
  );
}
